import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Panel } from '@components/Panel/Panel'
import { TokenImage } from '@components/Tokens/TokenSelector/TokenImage'
import { Icon, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useAutoRenewalSubscription } from 'hooks/useAutoRenewalSubscription'
import { useCombinedAuth } from 'privy/useCombinedAuth'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    blockchainQueryKeys,
    queryClient,
    SignerUndefinedError,
    SpaceAddressFromSpaceId,
    SpaceData,
    SpaceIdFromSpaceAddress,
    TransactionStatus,
    TransformedSubscription,
    useGetSingleSubscription,
    useMembershipStatus,
    useMyUserId,
    useSpaceDataWithId,
} from 'use-towns-client'
import { Address, formatEther, formatUnits } from 'viem'

import { useMyMembershipStatus } from '../RenewMembership/hooks/useMyMembershipStatus'
import { useShouldShowRenewMembership } from '../RenewMembership/hooks/useRenewCountdown'
import { useRenewMyMembership } from '../RenewMembership/hooks/useRenewMyMembership'
import { needsCurrencyMigration } from '../utils'

import { ConfirmDisableRenewal } from './ConfirmDisableRenewal'
import { useMembershipSubscriptionsStore } from './membershipSubscriptions.store'
import { trackSubscriptionUpdate } from './subscriptionAnalytics'
import { ExpiryDate, readableDuration, useSubscriptionDisplayData } from './SubscriptionsPanel'

export function SingleSubscriptionPanel() {
    const [searchParams] = useSearchParams()
    const spaceId = searchParams.get('spaceId') ?? undefined
    const space = useSpaceDataWithId(spaceId)
    const userId = useMyUserId()

    const { data: membershipStatus } = useMyMembershipStatus({ spaceId })

    const subscriptionDisplayData = useSubscriptionDisplayData({
        spaceId: spaceId ?? '',
        membershipStatus: {
            ...membershipStatus,
            isMember: membershipStatus?.isMember ?? false,
        },
        spaceName: space?.name ?? '',
    })
    const { data: myAddress } = useMyAbstractAccountAddress()

    const { data: subscription } = useGetSingleSubscription({
        walletAddress: myAddress ?? '',
        spaceAddress: SpaceAddressFromSpaceId(spaceId ?? '') as Address,
        tokenId: membershipStatus?.tokenId ?? '',
    })
    const isUsdc = subscriptionDisplayData.membershipInfo?.currency === 'USDC'

    // Migration detection: active ETH subscription in USDC space
    const needsMigration = useMemo(() => {
        if (!subscription?.active || !subscriptionDisplayData.membershipInfo?.currency) {
            return false
        }
        return needsCurrencyMigration(
            subscription.currency,
            subscriptionDisplayData.membershipInfo.currency,
        )
    }, [subscription, subscriptionDisplayData.membershipInfo?.currency])

    if (!spaceId || !space || !subscriptionDisplayData) {
        return null
    }
    return (
        <Panel label="Subscription">
            <Stack gap>
                <Stack gap="sm" background="level2" padding="md" rounded="md">
                    <Stack alignSelf="center">
                        <TokenImage
                            imgSrc={subscriptionDisplayData.token?.data.imgSrc}
                            width="x7"
                            background="level1"
                        />
                    </Stack>
                    <Stack gap="sm">
                        <Text strong size="lg">
                            {space.name}
                        </Text>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Text>
                                {isUsdc
                                    ? formatUnits(
                                          subscriptionDisplayData.membershipRenewalPrice ?? 0n,
                                          6,
                                      )
                                    : formatEther(
                                          subscriptionDisplayData.membershipRenewalPrice ?? 0n,
                                      )}{' '}
                                {subscriptionDisplayData.membershipInfo?.currency}
                            </Text>
                            <Stack
                                as="span"
                                rounded="full"
                                background="transparent40"
                                style={{
                                    width: '5px',
                                    height: '5px',
                                }}
                            />
                            <Text>
                                {readableDuration(
                                    subscriptionDisplayData.membershipInfo?.duration ?? 0,
                                )}
                            </Text>
                        </Stack>
                        {!needsMigration && subscriptionDisplayData.countdownData.expiryDate && (
                            <ExpiryDate
                                expiryDate={subscriptionDisplayData.countdownData.expiryDate}
                                active={subscription?.active}
                            />
                        )}
                    </Stack>
                </Stack>
                {!membershipStatus ? null : membershipStatus.isExpired ? (
                    <ExpiredMembership
                        space={space}
                        subscriptionDisplayData={subscriptionDisplayData}
                        duration={readableDuration(
                            subscriptionDisplayData.membershipInfo?.duration ?? 0,
                        )}
                    />
                ) : (
                    <ActiveMembership
                        space={space}
                        userId={userId}
                        membershipStatus={membershipStatus}
                        subscription={subscription}
                        subscriptionDisplayData={subscriptionDisplayData}
                        myAddress={myAddress}
                        needsMigration={needsMigration}
                    />
                )}
            </Stack>
        </Panel>
    )
}

export function ActiveMembership(props: {
    space: SpaceData
    userId: string | undefined
    myAddress: Address | undefined
    membershipStatus: ReturnType<typeof useMembershipStatus>['data']
    subscription: TransformedSubscription | undefined
    subscriptionDisplayData: ReturnType<typeof useSubscriptionDisplayData>
    needsMigration: boolean
}) {
    const {
        space,
        membershipStatus,
        subscription,
        subscriptionDisplayData,
        myAddress,
        needsMigration,
    } = props
    const [showConfirmDisableRenewal, setShowConfirmDisableRenewal] = useState(false)
    const spaceId = space.id
    const activeSubcription = subscription?.active

    const removeFailedSubscription = useMembershipSubscriptionsStore(
        (s) => s.removeFailedSubscription,
    )

    const { renewMembership, transactionStatus: renewTransactionStatus } = useRenewMyMembership({
        spaceId,
        location: 'subscriptions',
    })

    const isRenewing = renewTransactionStatus === TransactionStatus.Pending

    const handleRenewNow = (getSigner: GetSigner) => {
        renewMembership(getSigner, 'Renew Subscription')
    }
    const myUserId = useMyUserId()

    const trackSubscriptionEnable = (success: boolean) => {
        trackSubscriptionUpdate({
            spaceName: space.name,
            spaceId: space.id,
            duration: readableDuration(subscriptionDisplayData.membershipInfo?.duration ?? 0),
            type: 'enable',
            success,
        })
    }

    const { installAutoRenewal, transactionStatus: subscriptionTransactionStatus } =
        useAutoRenewalSubscription({
            onSuccess: async ({ spaceAddress, tokenId, isActivate, isMigrate }) => {
                await queryClient.refetchQueries({
                    queryKey: blockchainQueryKeys.membershipStatus(
                        SpaceIdFromSpaceAddress(spaceAddress),
                        myUserId ?? '',
                    ),
                })
                if (myAddress) {
                    await queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.subscriptions(myAddress),
                    })
                    await queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.singleSubscription(
                            myAddress,
                            spaceAddress,
                            tokenId,
                        ),
                    })
                }
                const message = isMigrate ? 'Subscription updated to USDC' : 'Auto-renewal enabled'
                popupToast(({ toast }) => <StandardToast.Success toast={toast} message={message} />)

                removeFailedSubscription(spaceAddress, tokenId)
                if (isActivate) {
                    trackSubscriptionEnable(true)
                }
            },
            onError: ({ isActivate, isMigrate }) => {
                const message = isMigrate
                    ? 'Failed to update subscription'
                    : 'Failed to enable auto-renewal'
                popupToast(({ toast }) => <StandardToast.Error toast={toast} message={message} />)

                if (isActivate) {
                    trackSubscriptionEnable(false)
                }
            },
        })

    const isInstalling = subscriptionTransactionStatus === TransactionStatus.Pending

    const handleMigration = async (getSigner: GetSigner) => {
        const tokenId = membershipStatus?.tokenId

        if (!spaceId || !tokenId || !myAddress) {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="Missing required data for migration" />
            ))
            return
        }

        const signer = await getSigner()
        if (!signer) {
            throw new SignerUndefinedError()
        }

        installAutoRenewal({
            spaceId,
            tokenId,
            signer,
            migrate: true,
        })
    }

    const handleInstallOrUninstall = async (getSigner: GetSigner) => {
        const tokenId = membershipStatus?.tokenId

        if (!spaceId) {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="No spaceId found" />
            ))
            return
        }
        if (!tokenId) {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="No tokenId found" />
            ))
            return
        }

        if (!myAddress) {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="No wallet address found" />
            ))
            return
        }

        const signer = await getSigner()
        if (!signer) {
            throw new SignerUndefinedError()
        }
        if (activeSubcription) {
            setShowConfirmDisableRenewal(true)
        } else {
            installAutoRenewal({
                spaceId,
                tokenId,
                signer,
            })
        }
    }
    const shouldShowRenewMembership = useShouldShowRenewMembership({ spaceId: spaceId ?? '' })

    if (!spaceId || !space || !subscriptionDisplayData) {
        return null
    }

    return (
        <Stack gap>
            {needsMigration ? (
                // Migration UI: show warning and update button
                <>
                    <Stack
                        horizontal
                        gap="sm"
                        alignItems="center"
                        background="level2"
                        padding="md"
                        rounded="sm"
                    >
                        <Icon type="alert" size="square_sm" color="negative" />
                        <Text color="negative" size="sm">
                            This space has been updated to USDC. Update your subscription to
                            continue.
                        </Text>
                    </Stack>
                    <SubscriptionButton
                        icon="arrowCircle"
                        label="Update Subscription"
                        background="level2"
                        color="neutral"
                        isLoading={isInstalling}
                        disabled={isRenewing || isInstalling}
                        onClick={handleMigration}
                    />
                </>
            ) : (
                // Normal enable/disable toggle UI
                <SubscriptionButton
                    icon={activeSubcription ? 'delete' : 'arrowCircle'}
                    label={activeSubcription ? 'Disable Auto-renewal' : 'Enable Auto-renewal'}
                    background="level2"
                    color={activeSubcription ? 'negative' : 'neutral'}
                    isLoading={isInstalling}
                    disabled={isRenewing || isInstalling}
                    onClick={handleInstallOrUninstall}
                />
            )}

            {!activeSubcription && !needsMigration && (
                <Stack gap>
                    <Text color="gray2">
                        You will be reminded to renew your subscription before expiration.{' '}
                        {shouldShowRenewMembership && (
                            <WalletReady LoginButton={<SimpleLoginButton />}>
                                {({ getSigner }) => (
                                    <>
                                        <Stack
                                            as="button"
                                            display="inline"
                                            background="none"
                                            cursor="pointer"
                                            textDecoration="underline"
                                            color="cta2"
                                            onClick={() => {
                                                handleRenewNow(getSigner)
                                            }}
                                        >
                                            <Text>Renew now</Text>
                                        </Stack>
                                    </>
                                )}
                            </WalletReady>
                        )}
                    </Text>
                    {isRenewing || isInstalling ? <ButtonSpinner /> : null}
                </Stack>
            )}
            {showConfirmDisableRenewal &&
                membershipStatus?.tokenId &&
                subscription?.entityId &&
                subscriptionDisplayData.countdownData.expiryDate &&
                myAddress && (
                    <ConfirmDisableRenewal
                        spaceAddress={SpaceAddressFromSpaceId(spaceId) as Address}
                        subscriptionDisplayData={subscriptionDisplayData}
                        myAddress={myAddress}
                        tokenId={membershipStatus?.tokenId ?? ''}
                        entityId={subscription?.entityId ?? 0}
                        spaceName={space.name}
                        expiryDate={subscriptionDisplayData.countdownData.expiryDate}
                        onCancel={() => setShowConfirmDisableRenewal(false)}
                    />
                )}
        </Stack>
    )
}

function ExpiredMembership(props: {
    space: SpaceData
    subscriptionDisplayData: ReturnType<typeof useSubscriptionDisplayData>
    duration: string
}) {
    const { space, subscriptionDisplayData, duration } = props
    const { openPanel } = usePanelActions()

    const { renewMembership, transactionStatus: renewTransactionStatus } = useRenewMyMembership({
        location: 'subscriptions',
        spaceId: space.id,
        onSuccess: () => {
            openPanel(CHANNEL_INFO_PARAMS.SUBSCRIPTIONS)
        },
    })
    const isUsdc = subscriptionDisplayData.membershipInfo?.currency === 'USDC'

    const isRenewing = renewTransactionStatus === TransactionStatus.Pending

    const handleRenewNow = (getSigner: GetSigner) => {
        renewMembership(getSigner, 'Renew Subscription')
    }

    return (
        <WalletReady>
            {({ getSigner }) => (
                <Stack
                    horizontal
                    gap="sm"
                    as="button"
                    background="level2"
                    alignItems="center"
                    padding="md"
                    rounded="sm"
                    cursor="pointer"
                    disabled={isRenewing}
                    opacity={isRenewing ? '0.5' : 'opaque'}
                    onClick={() => {
                        handleRenewNow(getSigner)
                    }}
                >
                    {isRenewing ? <ButtonSpinner /> : <Icon type="delete" size="square_sm" />}
                    <Text color="default">
                        Renew for{' '}
                        {isUsdc
                            ? formatUnits(subscriptionDisplayData.membershipRenewalPrice ?? 0n, 6)
                            : formatEther(
                                  subscriptionDisplayData.membershipRenewalPrice ?? 0n,
                              )}{' '}
                        {subscriptionDisplayData.membershipInfo?.currency} / {duration}
                    </Text>
                </Stack>
            )}
        </WalletReady>
    )
}

const SimpleLoginButton = () => {
    const { privyLogin } = useCombinedAuth()
    return (
        <Stack
            as="button"
            display="inline"
            background="none"
            cursor="pointer"
            textDecoration="underline"
            color="cta2"
            onClick={() => {
                privyLogin()
            }}
        >
            <Text>Reauthenticate to renew now</Text>
        </Stack>
    )
}

function SubscriptionButton({
    icon,
    label,
    background,
    color,
    isLoading,
    disabled,
    onClick,
}: {
    icon: 'arrowCircle' | 'delete'
    label: string
    background: 'cta1' | 'level2'
    color: 'default' | 'negative' | 'neutral'
    isLoading: boolean
    disabled: boolean
    onClick: (getSigner: GetSigner) => void
}) {
    return (
        <WalletReady>
            {({ getSigner }) => (
                <Stack
                    horizontal
                    as="button"
                    gap="sm"
                    background={background}
                    alignItems="center"
                    padding="md"
                    rounded="sm"
                    color={color}
                    cursor="pointer"
                    disabled={disabled}
                    opacity={disabled ? '0.5' : 'opaque'}
                    onClick={() => onClick(getSigner)}
                >
                    {isLoading ? <ButtonSpinner /> : <Icon type={icon} size="square_sm" />}
                    <Text>{label}</Text>
                </Stack>
            )}
        </WalletReady>
    )
}
